import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import referralApi from '../../services/referralApi';
import { fetchReferralMe } from '../../store/slices/referralSlice';
import {
  PageHeader,
  SegmentedToggle,
  EmptyState,
  TWButton,
} from '../../components/ui';
import ReferralHero from './components/ReferralHero';
import ReferralStats from './components/ReferralStats';
import ShareModal from './components/ShareModal';
import ReferralHistoryTable from './components/ReferralHistoryTable';
import ReferralLedgerTable from './components/ReferralLedgerTable';
import ReferralSkeleton from './components/ReferralSkeleton';
import './referral.css';

const HISTORY_PAGE_SIZE = 10;

const SHELL_CLASS =
  'referral-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';

const Referral = () => {
  const dispatch = useDispatch();
  const {
    data: meData,
    loading: meLoading,
    error: meError,
  } = useSelector((state) => state.referralReducer);

  const [loading, setLoading] = useState({ history: true, ledger: true });
  const [activeTab, setActiveTab] = useState('history');

  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyPagination, setHistoryPagination] = useState({
    pageNumber: 1,
    pageSize: HISTORY_PAGE_SIZE,
    totalRecords: 0,
  });

  const [ledger, setLedger] = useState([]);
  const [shareModal, setShareModal] = useState({ open: false, channel: 'sms' });

  const fetchHistory = useCallback(async (pageNumber, status) => {
    setLoading((prev) => ({ ...prev, history: true }));
    try {
      const statusPart = status !== '' ? `&status=${status}` : '';
      const query = `?pageNumber=${pageNumber}&pageSize=${HISTORY_PAGE_SIZE}${statusPart}`;
      const res = await referralApi.history(query);
      setHistory(res.data?.data || []);
      setHistoryPagination({
        pageNumber,
        pageSize: HISTORY_PAGE_SIZE,
        totalRecords: res.data?.totalRecords || 0,
      });
    } catch {
      setHistory([]);
      message.error('Failed to load referral history');
    } finally {
      setLoading((prev) => ({ ...prev, history: false }));
    }
  }, []);

  const fetchLedger = useCallback(async () => {
    setLoading((prev) => ({ ...prev, ledger: true }));
    try {
      const res = await referralApi.ledger();
      setLedger(res.data?.data || []);
    } catch {
      setLedger([]);
      message.error('Failed to load credit ledger');
    } finally {
      setLoading((prev) => ({ ...prev, ledger: false }));
    }
  }, []);

  // /Me redux slice'tan gelir (layout ile paylaşılır -> dedup).
  // History + Ledger sayfaya özel, lokal kalır.
  useEffect(() => {
    dispatch(fetchReferralMe());
    fetchHistory(1, '');
    fetchLedger();
  }, [dispatch, fetchHistory, fetchLedger]);

  const handleStatusChange = (value) => {
    setHistoryStatus(value);
    fetchHistory(1, value);
  };

  const handleHistoryPageChange = (page) => {
    fetchHistory(page, historyStatus);
  };

  const handleShare = (channel) => setShareModal({ open: true, channel });
  const closeShare = () => setShareModal((prev) => ({ ...prev, open: false }));

  // İlk paint: /Me çözülene kadar tam sayfa skeleton.
  if (meLoading && !meData) {
    return <ReferralSkeleton />;
  }

  // /Me başarısız -> hero/stat render edilemez; retry sun.
  if (meError && !meData) {
    return (
      <div className={SHELL_CLASS}>
        <div className="mx-auto max-w-5xl">
          <EmptyState
            title="Couldn't load referral data"
            description="Something went wrong while loading your referral dashboard."
            action={
              <TWButton
                variant="primary"
                label="Try again"
                onClick={() => dispatch(fetchReferralMe({ force: true }))}
              />
            }
          />
        </div>
      </div>
    );
  }

  // Program bu şirket için kapalı (menü zaten gizli — doğrudan erişim savunması).
  if (meData && meData.enabled === false) {
    return (
      <div className={SHELL_CLASS}>
        <div className="mx-auto max-w-5xl">
          <EmptyState
            title="Referral program is not available"
            description="The referral program is not available for your account right now."
          />
        </div>
      </div>
    );
  }

  // Rozet, History LİSTESİNİN gerçek sayısını gösterir (davet ettiğim
  // restoranlar). /Me'nin activecount+pendingcount'u beneficiary reward'ları
  // sayar (davet edilince kazanılan dahil) ve listeyle uyuşmaz — onu kullanmayız.
  const historyLabel = (
    <span className="inline-flex items-center gap-1.5">
      History
      {historyPagination.totalRecords > 0 ? (
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[0.65rem] font-bold tabular-nums text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {historyPagination.totalRecords}
        </span>
      ) : null}
    </span>
  );

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto max-w-5xl space-y-6">
        <PageHeader
          title="Refer & Earn"
          description="Invite other restaurants to Thisway and earn credit on your platform fees."
        />

        <ReferralHero data={meData} onShare={handleShare} />

        <ReferralStats data={meData} />

        <SegmentedToggle
          ariaLabel="Referral sections"
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'history', label: historyLabel },
            { value: 'ledger', label: 'Ledger' },
          ]}
        />

        {activeTab === 'history' ? (
          <ReferralHistoryTable
            data={history}
            loading={loading.history}
            pagination={historyPagination}
            onPageChange={handleHistoryPageChange}
            status={historyStatus}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <ReferralLedgerTable data={ledger} loading={loading.ledger} />
        )}

        <ShareModal
          open={shareModal.open}
          channel={shareModal.channel}
          onClose={closeShare}
        />
      </div>
    </div>
  );
};

export default Referral;
